import { Collapse, Nav, NavItem, NavLink } from 'reactstrap';
import Icon from 'components/Icon';
import { NavLink as NavLinkRouter, useNavigate } from 'react-router-dom';
import MenuProps from './_presenters/props/menuProps';
import useControllerMenu from './_presenters/useControllerMenu/useControllerMenu';
import classNames from 'classnames';
import NavCorporateTv from 'pages/CorporateTV/_presenters/enum/navCorporateTv';
import useUser from 'hooks/useUser';
import { useAdminCustomer } from 'contexts/adminCustomer';

const Menu = ({ isOpen, toggleSidebar, routes }: MenuProps) => {
  const {
    dropdownOpenTV,
    toggleMenuTV,
    dropdownOpenUser,
    toggleMenuUser,
    dropdownOpenDevices,
    toggleMenuDevices,
    dropdownOpenAdmin,
    toggleMenuAdmin
  } = useControllerMenu({ isOpen });

  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user?.role === 'ADMIN';
  const { hasCustomerContext, activeCustomer } = useAdminCustomer();

  // Se for admin, precisa ter contexto de cliente para ver seções de cliente
  // Se for customer, sempre tem acesso às seções de cliente
  const showCustomerSections = !isAdmin || hasCustomerContext;

  const goToTab = (tabName: unknown, baseRoute: string) => {
    navigate(`${baseRoute}?tab=${tabName}`);
  };

  return (
    <Nav className="d-flex justify-content-center" navbar>
      {/* Admin Menu - Only visible for ADMIN users */}
      {isAdmin && (
        <>
          <NavItem className="mb-2">
            <span
              className={classNames(
                'text-uppercase small text-muted fw-bold',
                isOpen ? 'ms-4' : 'd-none'
              )}
            >
              Administração
            </span>
          </NavItem>

          <NavItem>
            <NavLinkRouter
              className={classNames(
                'nav-link',
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              to={routes.ADMIN.DASHBOARD}
              end
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="speedometer2"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Dashboard
              </span>
            </NavLinkRouter>
          </NavItem>

          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              onClick={toggleMenuAdmin}
              role="button"
            >
              <button
                className="bg-transparent border-0 p-0"
                onClick={toggleSidebar}
              >
                <Icon
                  className={classNames(isOpen ? 'me-3' : 'me-5')}
                  icon="gear"
                  width={24}
                  height={24}
                />
              </button>
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Cadastros
              </span>
            </NavLink>
            <Collapse
              isOpen={dropdownOpenAdmin}
              className="bg-gray-100 w-100 py-2 nav-link-expanded"
              data-bs-popper="static"
            >
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  tag={NavLinkRouter}
                  to={routes.ADMIN.CUSTOMERS.LIST}
                >
                  <span className="ms-5 sub-menu-font fw-medium">Clientes</span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  tag={NavLinkRouter}
                  to={routes.ADMIN.USERS.LIST}
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    Usuários Admin
                  </span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  tag={NavLinkRouter}
                  to={routes.ADMIN.LICENCES.LIST}
                >
                  <span className="ms-5 sub-menu-font fw-medium">Licenças</span>
                </NavLink>
              </NavItem>
            </Collapse>
          </NavItem>

          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.ADMIN.NEWS_CHANNELS.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="rss"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Canais de Notícias
              </span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.ADMIN.NEWS_CATEGORIES.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="tags"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Categorias
              </span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.ADMIN.NOTIFICATIONS.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="bell"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Notificações
              </span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.ADMIN.SUPPORT_CONTACTS.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="headset"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Suporte
              </span>
            </NavLink>
          </NavItem>
        </>
      )}

      {/* Customer Section Header - Only visible for ADMIN when customer context */}
      {isAdmin && (
        <NavItem className="mt-4 mb-2">
          <span
            className={classNames(
              'text-uppercase small fw-bold',
              isOpen ? 'ms-4' : 'd-none',
              showCustomerSections ? 'text-muted' : 'text-warning'
            )}
          >
            {showCustomerSections
              ? `Cliente: ${activeCustomer?.name || 'Selecionado'}`
              : 'Selecione um cliente ↑'}
          </span>
        </NavItem>
      )}

      {/* Customer Sections - Only visible when customer context is available */}
      {showCustomerSections && (
        <>
          <NavItem>
            <NavLinkRouter
              className={classNames(
                'nav-link',
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              to="/"
              end
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="layers"
                width={24}
                height={24}
              />

              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Dashboard
              </span>
            </NavLinkRouter>
          </NavItem>
          <NavItem>
            <NavLink
              onClick={toggleMenuTV}
              role="button"
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.CORPORATE_TV.LIST}
            >
              <button
                className="bg-transparent border-0 p-0"
                onClick={toggleSidebar}
              >
                <Icon
                  className={classNames(isOpen ? 'me-3' : 'me-5')}
                  icon="tv"
                  width={24}
                  height={24}
                />
              </button>
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                TV Corporativa
              </span>
            </NavLink>
            <Collapse
              isOpen={dropdownOpenTV}
              className="bg-gray-100 w-100 py-2"
              data-bs-popper="static"
            >
              <NavItem tag="span">
                <NavLink
                  role="button"
                  className="ms-3 me-3 ps-1"
                  onClick={() =>
                    goToTab(NavCorporateTv.CAMPAIGNS, routes.CORPORATE_TV.LIST)
                  }
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    Campanhas
                  </span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() =>
                    goToTab(NavCorporateTv.PLAYLISTS, routes.CORPORATE_TV.LIST)
                  }
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    Playlists
                  </span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() =>
                    goToTab(NavCorporateTv.MEDIAS, routes.CORPORATE_TV.LIST)
                  }
                >
                  <span className="ms-5 sub-menu-font fw-medium">Mídias</span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() =>
                    goToTab(
                      NavCorporateTv.EXTERNAL_URL,
                      routes.CORPORATE_TV.LIST
                    )
                  }
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    URL Externa
                  </span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() =>
                    goToTab(
                      NavCorporateTv.INTERNAL_NEWS,
                      routes.CORPORATE_TV.LIST
                    )
                  }
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    Notícias Internas
                  </span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() =>
                    goToTab(
                      NavCorporateTv.EXTERNAL_NEWS,
                      routes.CORPORATE_TV.LIST
                    )
                  }
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    Notícias Externas
                  </span>
                </NavLink>
              </NavItem>
            </Collapse>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              onClick={toggleMenuDevices}
              role="button"
              tag={NavLinkRouter}
              to={routes.DEVICES.LIST}
            >
              <button
                className="bg-transparent border-0 p-0"
                onClick={toggleSidebar}
              >
                <Icon
                  className={classNames(isOpen ? 'me-3' : 'me-5')}
                  icon="laptop"
                  width={24}
                  height={24}
                />
              </button>
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Dispositivos
              </span>
            </NavLink>
            <Collapse
              isOpen={dropdownOpenDevices}
              className="bg-gray-100 w-100 py-2 nav-link-expanded"
              data-bs-popper="static"
            >
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() => goToTab('device-list', routes.DEVICES.LIST)}
                >
                  <span className="ms-5 fw-medium sub-menu-font">
                    Lista de Dispositivos
                  </span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  onClick={() => goToTab('device-group', routes.DEVICES.LIST)}
                >
                  <span className="ms-5 fw-medium sub-menu-font">
                    Grupos de Dispositivos
                  </span>
                </NavLink>
              </NavItem>
            </Collapse>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.CUSTOMER_COMPANIES.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="grid"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Empresas
              </span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              onClick={toggleMenuUser}
              role="button"
            >
              <button
                className="bg-transparent border-0 p-0"
                onClick={toggleSidebar}
              >
                <Icon
                  className={classNames(isOpen ? 'me-3' : 'me-5')}
                  icon="diagram-2"
                  width={24}
                  height={24}
                />
              </button>
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Usuários
              </span>
            </NavLink>
            <Collapse
              isOpen={dropdownOpenUser}
              className="bg-gray-100 w-100 py-2 nav-link-expanded"
              data-bs-popper="static"
            >
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  tag={NavLinkRouter}
                  to={routes.CUSTOMER_USERS.LIST}
                >
                  <span className="ms-5 sub-menu-font fw-medium">Usuários</span>
                </NavLink>
              </NavItem>
              <NavItem tag="span">
                <NavLink
                  className="ms-3 me-3 ps-1"
                  role="button"
                  tag={NavLinkRouter}
                  to={routes.ACCESS_POLICIES.LIST}
                >
                  <span className="ms-5 sub-menu-font fw-medium">
                    Políticas de acesso
                  </span>
                </NavLink>
              </NavItem>
            </Collapse>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.SUPPORT.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="question-circle"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Suporte
              </span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames(
                isOpen ? 'nav-link-expanded' : 'nav-link-collapsed',
                'ms-4'
              )}
              tag={NavLinkRouter}
              to={routes.HISTORY_LOGS.LIST}
            >
              <Icon
                className={classNames(isOpen ? 'me-3' : 'me-5')}
                icon="clock-history"
                width={24}
                height={24}
              />
              <span className={classNames(isOpen ? '' : 'd-none', 'fw-medium')}>
                Logs de Histórico
              </span>
            </NavLink>
          </NavItem>
        </>
      )}
    </Nav>
  );
};

export default Menu;
